import React, { useCallback, useMemo, useRef, useState } from "react";
import { useGetAgentSchedulesQuery } from "../../../app/api/console/agentSchedulerApiSlice";
import { useGetCustomAgentsQuery } from "../../../app/api/console/customAgentsApiSlice";
import {
  WEEKDAYS,
  agentSlugFromEndpoint,
  buildAddRecipientCommand,
  buildCreateScheduleCommand,
  buildCron,
  describeSchedule,
  groupSchedulesByCron,
  mergeRecipientEmails,
  recipientEmailsFromInput,
} from "./commandBuilder";
import ScheduleCalendar from "./ScheduleCalendar";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100";

function titleFromSlug(slug) {
  return slug
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function scheduleEnvironment(schedule) {
  if (schedule.name?.startsWith("zenyt-staging-")) return "staging";
  return schedule.input?.endpoint?.includes("staging.api.zenyt.ai")
    ? "staging"
    : "prod";
}

// The agent and website a schedule targets, resolved from the catalog of the
// environment the schedule runs in.
function resolveSchedule(schedule, agentsByEnvironmentAndSlug) {
  const slug = agentSlugFromEndpoint(schedule.input?.endpoint);
  const agent = agentsByEnvironmentAndSlug.get(
    `${scheduleEnvironment(schedule)}:${slug}`
  );
  const website = agent?.websites?.find(
    (item) => item.website_id === schedule.input?.website_id
  );
  return { slug, agent, website };
}

function scheduleOptionLabel(schedule, agentsByEnvironmentAndSlug) {
  const { slug, agent, website } = resolveSchedule(schedule, agentsByEnvironmentAndSlug);
  const websiteName =
    website?.website_name || schedule.input?.website_id || "Unknown website";
  const agentTitle = agent?.title || titleFromSlug(slug) || "Unknown agent";
  return `${websiteName} · ${agentTitle} (${schedule.name})`;
}

function StateDot({ state }) {
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${
        state === "ENABLED"
          ? "bg-green-500"
          : state === "DISABLED"
          ? "bg-red-500"
          : "bg-gray-400"
      }`}
      title={state || "Unknown state"}
      aria-label={state || "Unknown state"}
    />
  );
}

function stateSummary(schedules) {
  const counts = new Map();
  for (const schedule of schedules) {
    const state = (schedule.state || "unknown").toLowerCase();
    counts.set(state, (counts.get(state) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([state, count]) => `${count} ${state}`)
    .join(" · ");
}

function ScheduleRow({ schedule, agentsByEnvironmentAndSlug, onAddRecipient }) {
  const { slug, agent, website } = resolveSchedule(schedule, agentsByEnvironmentAndSlug);
  const recipients = recipientEmailsFromInput(schedule.input);
  return (
    <tr>
      <td className="py-3 pl-9 pr-3 text-xs text-gray-600">
        <div>{website?.website_name || "—"}</div>
        <div>{schedule.input?.website_id || "—"}</div>
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-gray-900">
          {agent?.title || titleFromSlug(slug) || "Unknown agent"}
        </div>
        <div className="text-xs text-gray-500">{slug || "—"}</div>
        <div className="text-xs text-gray-400">{schedule.name}</div>
      </td>
      <td className="px-3 py-3 text-xs text-gray-600">
        {recipients.length ? (
          <div className="space-y-1">
            {recipients.map((email) => (
              <div key={email} className="break-all">
                {email}
              </div>
            ))}
          </div>
        ) : (
          "—"
        )}
        <button
          type="button"
          onClick={() => onAddRecipient(schedule.name)}
          className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          Add recipient
        </button>
      </td>
      <td className="px-3 py-3">
        <StateDot state={schedule.state} />
      </td>
    </tr>
  );
}

function scheduleWebsiteName(schedule, agentsByEnvironmentAndSlug) {
  const { slug, agent, website } = resolveSchedule(schedule, agentsByEnvironmentAndSlug);
  return website?.website_name || agent?.title || titleFromSlug(slug) || schedule.name;
}

const VIEWS = [
  ["calendar", "Calendar"],
  ["list", "List"],
];

function AgentScheduler() {
  const [environment, setEnvironment] = useState("prod");
  const [agentSlug, setAgentSlug] = useState("");
  const [websiteId, setWebsiteId] = useState("");
  const [scheduleName, setScheduleName] = useState("");
  const [weekdays, setWeekdays] = useState(["TUE", "THU"]);
  const [hour, setHour] = useState("07");
  const [minute, setMinute] = useState("00");
  const [recipientEmails, setRecipientEmails] = useState("");
  const [auditDateMode, setAuditDateMode] = useState("live");
  const [siteDateOffsetDays, setSiteDateOffsetDays] = useState("1");
  const [siteDate, setSiteDate] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [view, setView] = useState("calendar");
  const [addScheduleName, setAddScheduleName] = useState("");
  const [addEmails, setAddEmails] = useState("");
  const [copiedCreate, setCopiedCreate] = useState(false);
  const [copiedAdd, setCopiedAdd] = useState(false);
  const addSectionRef = useRef(null);
  const addEmailRef = useRef(null);

  const schedulesQuery = useGetAgentSchedulesQuery();
  const prodAgentsQuery = useGetCustomAgentsQuery({ env: "prod" });
  const stagingAgentsQuery = useGetCustomAgentsQuery({ env: "staging" });
  const agentsQuery =
    environment === "prod" ? prodAgentsQuery : stagingAgentsQuery;
  const agents = agentsQuery.data?.agents || [];
  const selectedAgent = agents.find((agent) => agent.slug === agentSlug);
  const websites = selectedAgent?.websites || [];
  // Only agents that declare a date label act on an audit date; the API ignores
  // it for every other agent and audits the live site.
  const auditDateLabel = selectedAgent?.launch_date_label || "";
  const auditOffset = auditDateLabel && auditDateMode === "offset";
  const auditFixed = auditDateLabel && auditDateMode === "fixed";
  const agentsByEnvironmentAndSlug = useMemo(() => {
    const map = new Map();
    for (const [env, items] of [
      ["prod", prodAgentsQuery.data?.agents || []],
      ["staging", stagingAgentsQuery.data?.agents || []],
    ]) {
      for (const agent of items) map.set(`${env}:${agent.slug}`, agent);
    }
    return map;
  }, [prodAgentsQuery.data?.agents, stagingAgentsQuery.data?.agents]);

  const cron = buildCron({ minute, hour, weekdays });
  const validOffset = !auditOffset || /^\d{1,3}$/.test(siteDateOffsetDays.trim());
  const missingFixedDate = auditFixed && !siteDate;
  const command = useMemo(
    () =>
      // An empty offset or date would otherwise fall back to a live-site
      // schedule the operator did not ask for.
      !validOffset || missingFixedDate
        ? ""
        : buildCreateScheduleCommand({
            environment,
            scheduleName,
            agentSlug,
            websiteId,
            weekdays,
            hour,
            minute,
            recipientEmails,
            siteDateOffsetDays: auditOffset ? siteDateOffsetDays : "",
            siteDate: auditFixed ? siteDate : "",
          }),
    [
      environment,
      scheduleName,
      agentSlug,
      websiteId,
      weekdays,
      hour,
      minute,
      recipientEmails,
      auditOffset,
      auditFixed,
      siteDateOffsetDays,
      siteDate,
      validOffset,
      missingFixedDate,
    ]
  );
  const validName = !scheduleName || /^[A-Za-z0-9_.-]{1,64}$/.test(scheduleName);
  const schedules = useMemo(
    () => schedulesQuery.data?.schedules || [],
    [schedulesQuery.data?.schedules]
  );
  const scheduleGroups = useMemo(() => groupSchedulesByCron(schedules), [schedules]);
  const describeTarget = useCallback(
    (schedule) => {
      const { slug, agent, website } = resolveSchedule(schedule, agentsByEnvironmentAndSlug);
      return {
        website: website?.website_name || schedule.input?.website_id || schedule.name,
        agent: agent?.title || titleFromSlug(slug) || "Unknown agent",
      };
    },
    [agentsByEnvironmentAndSlug]
  );
  const selectedAddSchedule = schedules.find(
    (schedule) => schedule.name === addScheduleName
  );
  const currentAddRecipients = recipientEmailsFromInput(selectedAddSchedule?.input);
  const nextAddRecipients = mergeRecipientEmails(currentAddRecipients, addEmails);
  const addCommand = useMemo(
    () => buildAddRecipientCommand(selectedAddSchedule, addEmails),
    [selectedAddSchedule, addEmails]
  );

  const selectEnvironment = (value) => {
    setEnvironment(value);
    setAgentSlug("");
    setWebsiteId("");
    setScheduleName("");
    setAuditDateMode("live");
  };

  const selectAgent = (slug) => {
    setAgentSlug(slug);
    setAuditDateMode("live");
    const agent = agents.find((item) => item.slug === slug);
    setWebsiteId(agent?.websites?.[0]?.website_id || "");
    setScheduleName(slug ? `zenyt-${environment}-sched-${slug}` : "");
  };

  const toggleDay = (day) => {
    setWeekdays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : WEEKDAYS.map(([value]) => value).filter(
            (value) => current.includes(value) || value === day
          )
    );
  };

  const toggleGroup = (key) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const copyCreateCommand = async () => {
    if (!command || !validName) return;
    await navigator.clipboard.writeText(command);
    setCopiedCreate(true);
    window.setTimeout(() => setCopiedCreate(false), 1500);
  };

  const copyAddCommand = async () => {
    if (!addCommand) return;
    await navigator.clipboard.writeText(addCommand);
    setCopiedAdd(true);
    window.setTimeout(() => setCopiedAdd(false), 1500);
  };

  const startAddRecipient = (name) => {
    setAddScheduleName(name);
    setAddEmails("");
    setCopiedAdd(false);
    window.requestAnimationFrame(() => {
      addSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      addEmailRef.current?.focus();
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900">Agent scheduler</h1>
        <p className="mt-1 text-sm text-gray-600">
          Read-only AWS inventory and command generator. This page cannot create,
          update, or delete schedules.
        </p>
      </header>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-900">Existing schedules</h2>
            <p className="text-xs text-gray-500">
              Showing zenyt-prod-sched-* and zenyt-staging-sched-* from us-east-1.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div
              role="group"
              aria-label="Schedule view"
              className="inline-flex rounded-lg border border-gray-300 p-0.5"
            >
              {VIEWS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setView(value)}
                  aria-pressed={view === value}
                  className={`rounded-md px-3 py-1.5 text-sm ${
                    view === value
                      ? "bg-indigo-600 font-medium text-white"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={schedulesQuery.refetch}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {schedulesQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading schedules…</p>
        ) : schedulesQuery.isError ? (
          <p className="text-sm text-red-600">
            {schedulesQuery.error?.data?.detail || "Could not read AWS schedules."}
          </p>
        ) : view === "calendar" ? (
          <ScheduleCalendar schedules={schedules} describeTarget={describeTarget} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Website</th>
                  <th className="px-3 py-2">Agent</th>
                  <th className="px-3 py-2">Recipients</th>
                  <th className="px-3 py-2">State</th>
                </tr>
              </thead>
              {scheduleGroups.map((group) => {
                const expanded = expandedGroups.has(group.key);
                const description = describeSchedule(group.expression, group.timezone);
                const names = group.schedules.map((schedule) =>
                  scheduleWebsiteName(schedule, agentsByEnvironmentAndSlug)
                );
                const preview =
                  names.length > 3
                    ? `${names.slice(0, 3).join(", ")} +${names.length - 3} more`
                    : names.join(", ");
                return (
                  <tbody
                    key={group.key}
                    className="divide-y divide-gray-100 border-t border-gray-200"
                  >
                    <tr className="bg-gray-50/60">
                      <td colSpan="4" className="p-0">
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.key)}
                          aria-expanded={expanded}
                          className="flex w-full items-start gap-3 px-3 py-3 text-left hover:bg-gray-100"
                        >
                          <span
                            aria-hidden="true"
                            className={`mt-0.5 inline-block w-3 text-xs text-gray-500 transition-transform ${
                              expanded ? "rotate-90" : ""
                            }`}
                          >
                            ▶
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                              <span className="font-medium text-gray-900">
                                {description}
                              </span>
                              {description !== group.expression && (
                                <span className="font-mono text-xs text-gray-500">
                                  {group.expression}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                {group.schedules.length}{" "}
                                {group.schedules.length === 1 ? "schedule" : "schedules"}
                                {" · "}
                                {stateSummary(group.schedules)}
                              </span>
                            </span>
                            {!expanded && (
                              <span className="mt-1 block truncate text-xs text-gray-500">
                                {preview}
                              </span>
                            )}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {expanded &&
                      group.schedules.map((schedule) => (
                        <ScheduleRow
                          key={`${schedule.group_name}:${schedule.name}`}
                          schedule={schedule}
                          agentsByEnvironmentAndSlug={agentsByEnvironmentAndSlug}
                          onAddRecipient={startAddRecipient}
                        />
                      ))}
                  </tbody>
                );
              })}
              {!schedules.length && (
                <tbody>
                  <tr>
                    <td colSpan="4" className="px-3 py-6 text-center text-gray-500">
                      No matching schedules found.
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        )}
      </section>

      <section
        ref={addSectionRef}
        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <h2 className="font-semibold text-gray-900">Add a notification recipient</h2>
        <p className="mb-5 mt-1 text-xs text-gray-500">
          Generates an AWS CLI update command. This page does not change the
          schedule; copy the command and run it in your terminal.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            Existing schedule
            <select
              className={`${inputClass} mt-1`}
              value={addScheduleName}
              onChange={(event) => {
                setAddScheduleName(event.target.value);
                setCopiedAdd(false);
              }}
            >
              <option value="">Select a schedule</option>
              {schedules.map((schedule) => (
                <option key={`${schedule.group_name}:${schedule.name}`} value={schedule.name}>
                  {scheduleOptionLabel(schedule, agentsByEnvironmentAndSlug)}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm font-medium text-gray-700">
            Email to add
            <input
              ref={addEmailRef}
              className={`${inputClass} mt-1`}
              value={addEmails}
              onChange={(event) => setAddEmails(event.target.value)}
              placeholder="name@example.com"
            />
          </label>
        </div>

        {selectedAddSchedule && (
          <div className="mt-4 text-sm text-gray-600">
            <div>
              <span className="font-medium text-gray-700">Current recipients: </span>
              {currentAddRecipients.length
                ? currentAddRecipients.join(", ")
                : "none"}
            </div>
            {addCommand && (
              <div className="mt-1">
                <span className="font-medium text-gray-700">After this command: </span>
                {nextAddRecipients.join(", ")}
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">AWS CLI command</span>
            <button
              type="button"
              onClick={copyAddCommand}
              disabled={!addCommand}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {copiedAdd ? "Copied" : "Copy command"}
            </button>
          </div>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-950 p-4 text-xs text-gray-100">
            {addCommand ||
              (selectedAddSchedule &&
              addEmails.trim() &&
              nextAddRecipients.length === currentAddRecipients.length
                ? "That address is already on this schedule."
                : "Select a schedule and enter an email to generate the command.")}
          </pre>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-gray-900">Generate create command</h2>
        <p className="mb-5 mt-1 text-xs text-gray-500">
          Times are in ET (America/New_York, including daylight saving).
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            Environment
            <select
              className={`${inputClass} mt-1`}
              value={environment}
              onChange={(event) => selectEnvironment(event.target.value)}
            >
              <option value="prod">Production</option>
              <option value="staging">Staging</option>
            </select>
          </label>

          <label className="text-sm font-medium text-gray-700">
            Agent
            <select
              className={`${inputClass} mt-1`}
              value={agentSlug}
              onChange={(event) => selectAgent(event.target.value)}
              disabled={agentsQuery.isLoading}
            >
              <option value="">
                {agentsQuery.isLoading ? "Loading agents…" : "Select an agent"}
              </option>
              {agents.map((agent) => (
                <option key={agent.slug} value={agent.slug}>
                  {agent.title || agent.slug}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm font-medium text-gray-700">
            Website
            <select
              className={`${inputClass} mt-1`}
              value={websiteId}
              onChange={(event) => setWebsiteId(event.target.value)}
              disabled={!agentSlug}
            >
              <option value="">Select a website</option>
              {websites.map((website) => (
                <option key={website.website_id} value={website.website_id}>
                  {website.website_name || website.website_id}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm font-medium text-gray-700">
            Schedule name
            <input
              className={`${inputClass} mt-1 ${validName ? "" : "border-red-500"}`}
              value={scheduleName}
              onChange={(event) => setScheduleName(event.target.value)}
              placeholder={`zenyt-${environment}-sched-brand-agent`}
            />
            {!validName && (
              <span className="mt-1 block text-xs text-red-600">
                Use 1–64 letters, numbers, hyphens, underscores, or periods.
              </span>
            )}
          </label>
        </div>

        <div className="mt-5">
          <span className="text-sm font-medium text-gray-700">Weekdays</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {WEEKDAYS.map(([value, label]) => (
              <label
                key={value}
                className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${
                  weekdays.includes(value)
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                    : "border-gray-300 text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={weekdays.includes(value)}
                  onChange={() => toggleDay(value)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <label className="text-sm font-medium text-gray-700">
            Hour (ET)
            <select
              className={`${inputClass} mt-1`}
              value={hour}
              onChange={(event) => setHour(event.target.value)}
            >
              {Array.from({ length: 24 }, (_, value) => (
                <option key={value} value={String(value).padStart(2, "0")}>
                  {String(value).padStart(2, "0")}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Minute
            <select
              className={`${inputClass} mt-1`}
              value={minute}
              onChange={(event) => setMinute(event.target.value)}
            >
              {["00", "15", "30", "45"].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm font-medium text-gray-700">
            AWS cron
            <div className="mt-1 rounded-lg bg-gray-100 px-3 py-2 font-mono text-sm">
              {cron}
            </div>
          </div>
        </div>

        <label className="mt-5 block text-sm font-medium text-gray-700">
          Notification recipients
          <textarea
            className={`${inputClass} mt-1 min-h-24`}
            value={recipientEmails}
            onChange={(event) => setRecipientEmails(event.target.value)}
            placeholder="one@example.com, two@example.com"
          />
        </label>

        {auditDateLabel && (
          <div className="mt-5">
            <span className="text-sm font-medium text-gray-700">{auditDateLabel}</span>
            <div className="mt-2 grid gap-4 md:grid-cols-3">
              <label className="text-sm font-medium text-gray-700">
                Audit the site as of
                <select
                  className={`${inputClass} mt-1`}
                  value={auditDateMode}
                  onChange={(event) => setAuditDateMode(event.target.value)}
                >
                  <option value="live">The day of each run (live site)</option>
                  <option value="offset">A number of days after each run</option>
                  <option value="fixed">A fixed date</option>
                </select>
              </label>
              {auditDateMode === "offset" && (
                <label className="text-sm font-medium text-gray-700">
                  Days after each run
                  <input
                    type="number"
                    min="0"
                    max="999"
                    step="1"
                    className={`${inputClass} mt-1 ${validOffset ? "" : "border-red-500"}`}
                    value={siteDateOffsetDays}
                    onChange={(event) => setSiteDateOffsetDays(event.target.value)}
                  />
                  {!validOffset && (
                    <span className="mt-1 block text-xs text-red-600">
                      Use a whole number of days (0 or more).
                    </span>
                  )}
                </label>
              )}
              {auditDateMode === "fixed" && (
                <label className="text-sm font-medium text-gray-700">
                  Date
                  <input
                    type="date"
                    className={`${inputClass} mt-1`}
                    value={siteDate}
                    onChange={(event) => setSiteDate(event.target.value)}
                  />
                </label>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {auditDateMode === "offset"
                ? "Each run audits the storefront as it will look that many days after the run's date, at noon (the same default as the Hub's date picker)."
                : auditDateMode === "fixed"
                ? "Every run audits the storefront as of this date, at noon, until the schedule is changed."
                : "Leave on the live site unless the run should preview a scheduled campaign."}
              {auditDateMode === "offset" && hour === "00" &&
                " Runs that start between 00:00 and 00:59 ET during daylight time count from the previous day."}
            </p>
          </div>
        )}

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">AWS CLI command</span>
            <button
              type="button"
              onClick={copyCreateCommand}
              disabled={!command || !validName}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {copiedCreate ? "Copied" : "Copy command"}
            </button>
          </div>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-950 p-4 text-xs text-gray-100">
            {command ||
              (agentSlug && websiteId && missingFixedDate
                ? "Pick the audit date to generate the command."
                : agentSlug && websiteId && !validOffset
                ? "Enter a whole number of days to generate the command."
                : "Select an agent and website to generate the command.")}
          </pre>
        </div>
      </section>
    </div>
  );
}

export default AgentScheduler;
